import React, { useEffect, useState } from "react";
import classNames from "classnames";
import { useProgram } from "../utils/useProgram";
import { useParams } from "react-router-dom";
import { StatusLabelMap, SuggestionType, UpdateStatus } from "../utils/enums";
import Pill from "@shared/components/Pill";

interface Suggestion {
  id: number;
  suggestion_type: string;
  target_id: number;
  target_name: string;
  current_value: string;
  suggested_value: string;
  source: string;
  status: UpdateStatus;
  created_at: string;
  applied_at: string | null;
  applied_by: string | null;
}

interface ImpactAssessmentProps {
  updateId: string;
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
}

const ImpactAssessment: React.FC<ImpactAssessmentProps> = ({
  updateId,
  setShouldRefresh,
}) => {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);

  const allApplied = suggestions.every((s) => s.status === "applied");
  const allIgnored = suggestions.every((s) => s.status === "ignored");

  const {
    getSuggestions,
    applySuggestion,
    ignoreSuggestion,
    ignoreAllSuggestions,
    applyAllSuggestions,
  } = useProgram();

  const [loading, setLoading] = useState<string | null>(null);

  const { programId } = useParams();

  const fetchSuggestions = async () => {
    if (programId && updateId) {
      try {
        const result = await getSuggestions(programId, parseInt(updateId));
        setSuggestions(result.suggestions);
      } catch (error) {
        console.error("Failed to fetch suggestions:", error);
      }
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, [updateId, programId, updateId]);

  const applyAll = async () => {
    if (!programId || !updateId) return;

    try {
      setLoading("apply_all");
      await applyAllSuggestions(programId, parseInt(updateId));
      setShouldRefresh(true);
      fetchSuggestions();
    } catch (error) {
      console.error("Failed to apply all suggestions:", error);
    } finally {
      setLoading(null);
    }

    setSuggestions((prev) =>
      prev.map((s) => ({ ...s, status: UpdateStatus.applied }))
    );
  };

  const ignoreAll = async () => {
    if (!programId || !updateId) return;

    try {
      setLoading("ignore_all");
      await ignoreAllSuggestions(programId, parseInt(updateId));
      fetchSuggestions();
    } catch (error) {
      console.error("Failed to apply all suggestions:", error);
    } finally {
      setLoading(null);
    }
  };

  const handleApply = async (suggestionId: number) => {
    if (!programId || !updateId) return;

    try {
      setLoading(`apply-${suggestionId}`);
      await applySuggestion(programId, suggestionId);
      setSuggestions((prev) =>
        prev.map((s) =>
          s.id === suggestionId ? { ...s, status: UpdateStatus.applied } : s
        )
      );
      setShouldRefresh(true);
    } catch (error) {
      console.error(`Failed to apply suggestion ${suggestionId}:`, error);
    } finally {
      setLoading(null);
    }
  };

  const handleIgnore = async (suggestionId: number) => {
    if (!programId || !updateId) return;

    try {
      setLoading(`ignore-${suggestionId}`);
      await ignoreSuggestion(programId, suggestionId);
      setSuggestions((prev) =>
        prev.map((s) =>
          s.id === suggestionId ? { ...s, status: UpdateStatus.ignored } : s
        )
      );
    } catch (error) {
      console.error(`Failed to ignore suggestion ${suggestionId}:`, error);
    } finally {
      setLoading(null);
    }
  };

  const formatStatusValue = (value: string): string => {
    return StatusLabelMap[value?.toLowerCase()] || value;
  };

  if (suggestions.length === 0) return null;

  return (
    <div className="p-0 flex-1 flex flex-col">
      <div className="flex justify-between items-center mb-4 border-b border-gray-300 px-8 py-4">
        <h2 className="text-lg 2xl:text-xl font-semibold ">
          Impact Assessment
        </h2>
        <div className="flex gap-2">
          {suggestions.filter((s) => s.status === "pending").length > 0 && (
            <>
              <button
                onClick={applyAll}
                disabled={allApplied}
                className="bg-[#fff] text-gray-800 text-xs font-semibold border border-gray-400 px-4 py-1.5 rounded hover:bg-gray-50 cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400 "
              >
                Apply All
              </button>
              <button
                onClick={ignoreAll}
                disabled={allIgnored}
                className="bg-[#fff] text-gray-800 text-xs font-semibold border border-gray-400 px-4 py-1.5 rounded hover:bg-gray-50 cursor-pointer disabled:cursor-not-allowed disabled:text-gray-400 "
              >
                Ignore All
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1"></div>

      <div className="space-y-4 px-8 overflow-y-auto max-h-[30vh] pb-4">
        {suggestions.map((s) => (
          <div
            key={s.id}
            className={classNames("border p-4 rounded-lg  transition-all", {
              "bg-green-50 border-green-500": s.status === "applied",
              "bg-red-50 border-red-400": s.status === "ignored",
              "bg-[#FEF9ED] border-yellow-600": s.status === "pending",
            })}
          >
            <div className="flex justify-between items-start">
              <div className="flex items-center space-x-2 mb-3">
                <Pill
                  styling="text-gray-600 border-gray-600 bg-gray-50 py-[0px!important]"
                  text={SuggestionType[s.suggestion_type]}
                  small
                />

                <h3 className="font-semibold text-base">{s.target_name}</h3>
              </div>
              <div className="flex items-center gap-2">
                {s.status === "applied" && (
                  <div className="flex items-center space-x-1">
                    <img
                      src="/status/checkmark.svg"
                      className="w-5 h-5"
                      alt=""
                    />
                    <span className="text-xs 2xl:text-sm text-green-700 font-medium">
                      Applied
                    </span>
                  </div>
                )}
                {s.status === "ignored" && (
                  <div className="flex items-center space-x-1">
                    <img
                      src="/status/x-circle.svg"
                      className="w-5 h-5"
                      alt=""
                    />
                    <span className="text-xs 2xl:text-sm text-red-600 font-medium">
                      Ignored
                    </span>
                  </div>
                )}
                {s.status === "pending" && (
                  <>
                    <button
                      onClick={() => handleApply(s.id)}
                      disabled={loading === `apply-${s.id}`}
                      title="Apply"
                    >
                      <img src="/status/checkmark.svg" alt="" />
                    </button>
                    <button
                      onClick={() => handleIgnore(s.id)}
                      disabled={loading === `ignore-${s.id}`}
                      title="Ignore"
                    >
                      <img src="/status/x-circle.svg" alt="" />
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="mt-2 text-gray-800 text-xs 2xl:text-sm ">
              <div className="">
                <span className="text-gray-500 font-semibold mb-2">
                  Current:
                </span>{" "}
                {formatStatusValue(s.current_value)}
              </div>
              <div className="mt-1">
                <span className="text-gray-500 font-semibold mb-2">
                  Suggested:
                </span>{" "}
                <strong className="text-blue-600 font-medium">
                  {formatStatusValue(s.suggested_value)}
                </strong>
              </div>
              <div className="mt-1">
                <span className="text-gray-500">Source:</span> {s.source}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ImpactAssessment;
